import { DOMParser as PMDOMParser, DOMSerializer, Slice } from '@tiptap/pm/model';
import { countWords, htmlToMd, mdToHtml } from '../../markdown/wikiMarkdown';

/**
 * Markdown in and out of the editor — always through the shared engine.
 *
 *   in:  md → mdToHtml(md) (editor-HTML contract) → ProseMirror doc
 *   out: doc → DOM via the schema's renderHTML (the same contract) → htmlToMd
 *
 * HTML strings are parsed with the browser's DOMParser into an inert document
 * (nothing runs, nothing loads) and whitespace is kept exactly as written
 * (`preserveWhitespace: 'full'`) so the editor's round trip equals the
 * engine's `htmlToMd(mdToHtml(md))` byte for byte.
 */

const PARSE_OPTIONS = Object.freeze({ preserveWhitespace: 'full' });

function inertBody(html) {
    return new window.DOMParser().parseFromString(`<!doctype html><body>${html}`, 'text/html').body;
}

/** Editor HTML (the contract) → ProseMirror doc. */
export function htmlToDoc(html, schema) {
    return PMDOMParser.fromSchema(schema).parse(inertBody(html), PARSE_OPTIONS);
}

/** Markdown → ProseMirror doc. */
export function markdownToDoc(md, schema) {
    return htmlToDoc(mdToHtml(md || ''), schema);
}

/**
 * Markdown → a maximally open slice for pasting (prototype: insertHTML of
 * wkMdToHtml(text)). Open ends let a one-paragraph paste merge into the
 * current line; isolating nodes (tables, cells) are never opened.
 */
export function markdownToSlice(md, schema) {
    const doc = markdownToDoc(md, schema);
    return Slice.maxOpen(doc.content, false);
}

/** ProseMirror doc → a detached DOM container holding the editor-HTML contract. */
export function docToDom(doc, schema) {
    const container = document.createElement('div');
    container.appendChild(DOMSerializer.fromSchema(schema).serializeFragment(doc.content, { document }));
    return container;
}

/** ProseMirror doc → editor HTML string (tests / debugging). */
export function docToHtml(doc, schema) {
    return docToDom(doc, schema).innerHTML;
}

/** ProseMirror doc → Markdown, through the engine's wkHtmlToMd port. */
export function docToMarkdown(doc, schema) {
    return htmlToMd(docToDom(doc, schema));
}

/**
 * Prototype word count over the Write surface. Blocks are separated by a
 * newline (the prototype counted `textContent`, which glued the last word of
 * one block to the first word of the next — fixed here).
 */
export function docWordCount(doc) {
    return countWords(doc.textBetween(0, doc.content.size, '\n', '\n'));
}
